import type { UseFormReturn } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { CountrySelect } from "@/components/ui/country-select";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { LegalFormSelect } from "@/components/common/LegalFormSelect";
import { getJurisdictionIdentityConfig } from "@/config/jurisdictionIdentity";
import { useTranslation } from "@/hooks/useTranslation";
import { LocationSelector } from "../LocationSelector";
import { PhoneField, SectionHead, TextField } from "./fields";
import type { BusinessIdentityFormValues, LocationDraft, PersonFormValues } from "./personFormSchema";

interface PersonDetailsSectionProps {
  form: UseFormReturn<PersonFormValues>;
  businessForm: UseFormReturn<BusinessIdentityFormValues>;
  locations: LocationDraft[];
  onLocationsChange: (locations: LocationDraft[]) => void;
  /** The person already has their own business record (linked_business_id). */
  hasLinkedBusiness?: boolean;
  /**
   * Show the vendor bank identity fields (trading name … BIC). Accounting data
   * since org roles step 3 — only a role with the `accounting` scope sees them;
   * everyone else keeps just the business name.
   */
  showVendorIdentity?: boolean;
}

/**
 * The Details tab of the person form: identity, contact (in the tinted band,
 * with the person's locations), professional details, and — when "is a
 * business" is ticked — the business identity written to the person's own
 * business record.
 */
export function PersonDetailsSection({
  form,
  businessForm,
  locations,
  onLocationsChange,
  hasLinkedBusiness = false,
  showVendorIdentity = true,
}: PersonDetailsSectionProps) {
  const { t } = useTranslation("people");
  const control = form.control;
  const isBusiness = form.watch("is_business");
  const businessCountry = businessForm.watch("country_code");
  const jurisdictionConfig = getJurisdictionIdentityConfig(businessCountry || null);

  const checkbox = (name: "has_system_access", labelKey: string, hintKey: string) => (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
          <FormControl>
            <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel>{t(labelKey)}</FormLabel>
            <p className="text-sm text-muted-foreground">{t(hintKey)}</p>
          </div>
        </FormItem>
      )}
    />
  );

  return (
    <div className="space-y-8">
      <section>
        <SectionHead>{t("form.sections.identity")}</SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextField control={control} name="first_name" label={t("form.fields.firstName")} />
          <TextField control={control} name="last_name" label={t("form.fields.lastName")} />
          <TextField control={control} name="middle_name" label={t("form.fields.middleName")} />
          <TextField
            control={control}
            name="preferred_name"
            label={t("form.fields.preferredName")}
            placeholder={t("form.placeholders.preferredName")}
          />
        </div>
      </section>

      {/* Tinted band — theme-aware `bg-muted`. The person form alternates
          bands down the page (FULLSCREEN_FORM_MODALS.md sanctioned deviation). */}
      <div className="-mx-6 px-6 py-6 bg-muted space-y-6">
        <section>
          <SectionHead>{t("form.sections.contact")}</SectionHead>
          {/* Emails in halves, the three phones in thirds (a 6-column track). */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-3">
              <TextField control={control} name="email" type="email" label={t("form.fields.email")} />
            </div>
            <div className="md:col-span-3">
              <TextField control={control} name="secondary_email" type="email" label={t("form.fields.secondaryEmail")} />
            </div>
            <div className="md:col-span-2">
              <PhoneField control={control} name="phone" label={t("form.fields.phone")} placeholder={t("form.placeholders.phone")} />
            </div>
            <div className="md:col-span-2">
              <PhoneField
                control={control}
                name="mobile_phone"
                label={t("form.fields.mobilePhone")}
                placeholder={t("form.placeholders.phone")}
              />
            </div>
            <div className="md:col-span-2">
              <PhoneField
                control={control}
                name="work_phone"
                label={t("form.fields.workPhone")}
                placeholder={t("form.placeholders.phone")}
              />
            </div>
          </div>
        </section>
        <LocationSelector value={locations} onChange={onLocationsChange} />
      </div>

      <section className="space-y-4">
        <SectionHead>{t("form.sections.professional")}</SectionHead>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField control={control} name="company" label={t("form.fields.company")} />
          <TextField control={control} name="job_title" label={t("form.fields.jobTitle")} />
          <TextField control={control} name="department" label={t("form.fields.department")} />
          <TextField
            control={control}
            name="linkedin_url"
            label={t("form.fields.linkedin")}
            placeholder={t("form.placeholders.linkedin")}
          />
          <TextField control={control} name="website" label={t("form.fields.website")} placeholder="https://" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField control={control} name="bio" label={t("form.fields.bio")} multiline />
          <TextField control={control} name="notes" label={t("form.fields.notes")} multiline />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {checkbox("has_system_access", "form.fields.systemAccess", "form.hints.systemAccess")}
        </div>
      </section>

      {/* Tinted band. Always shown so people know the section exists; the
          fields stay disabled until "is a business" is ticked. */}
      <section className="-mx-6 px-6 py-6 bg-muted space-y-4">
        <div className="flex items-center justify-between gap-4">
          <SectionHead className="mb-0">{t("form.sections.business")}</SectionHead>
          <FormField
            control={control}
            name="is_business"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                <FormControl>
                  {/* White fill — it sits on the tinted band, where the
                      unfilled primitive would show the grey through. */}
                  <Checkbox
                    className="bg-background"
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                </FormControl>
                <FormLabel className="font-normal">{t("form.fields.isBusiness")}</FormLabel>
              </FormItem>
            )}
          />
        </div>
        <p className="text-sm text-muted-foreground">{t("form.hints.business")}</p>
        {/* Unticking only clears the flag — the business record and every
            document that names it stay. Say so before the user saves. */}
        {hasLinkedBusiness && !isBusiness && (
          <p className="text-sm text-muted-foreground" data-testid="business-kept-note">
            {t("form.hints.businessKept")}
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextField
            control={businessForm.control}
            name="business_name"
            disabled={!isBusiness}
            label={t("form.fields.businessName")}
            placeholder={t("form.placeholders.businessName")}
          />
          {showVendorIdentity && (
          <>
          <TextField
            control={businessForm.control}
            name="trading_name"
            disabled={!isBusiness}
            label={t("form.fields.tradingName")}
            placeholder={t("form.placeholders.tradingName")}
          />
          <FormField
            control={businessForm.control}
            name="country_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.fields.country")}</FormLabel>
                <FormControl>
                  <CountrySelect value={field.value || ""} onValueChange={field.onChange} disabled={!isBusiness} />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <Label>{t("form.fields.legalForm")}</Label>
            <LegalFormSelect
              countryCode={businessCountry || null}
              value={businessForm.watch("legal_form_id")}
              onValueChange={(value) => businessForm.setValue("legal_form_id", value, { shouldDirty: true })}
              disabled={!isBusiness}
            />
          </div>
          <TextField
            control={businessForm.control}
            name="registration_number"
            disabled={!isBusiness}
            label={jurisdictionConfig.registrationNumber.label}
            placeholder={jurisdictionConfig.registrationNumber.format ?? ""}
          />
          <TextField
            control={businessForm.control}
            name="vat_number"
            disabled={!isBusiness}
            label={jurisdictionConfig.vatNumber.label}
            placeholder={jurisdictionConfig.vatNumber.format ?? ""}
          />
          <TextField control={businessForm.control} name="iban" label={t("form.fields.iban")} disabled={!isBusiness} />
          <TextField control={businessForm.control} name="bic" label={t("form.fields.bic")} disabled={!isBusiness} />
          </>
          )}
        </div>
      </section>
    </div>
  );
}
